import {
  Box,
  Container,
  Grid,
  Typography,
  useTheme,
  TextField,
  Button,
  Snackbar,
  Alert,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { tokens } from "../../theme";
import { motion } from "framer-motion";
import TopBarOfMarketing from "../../components/TopBarOfMarketing";
import Footer from "../../components/Footer";
import { useTranslation } from "react-i18next";
import {
  getGoogleFormContactConfig,
  postGoogleFormInHiddenIframe,
  GOOGLE_FORM_HIDDEN_IFRAME_NAME,
} from "../../config/googleFormContact";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import Seo from "../../seo/Seo";
import { ORGANIZATION_JSONLD } from "../../seo/seoConfig";
import { useRecaptchaToken, RECAPTCHA_ACTIONS } from "../../utils/recaptcha";

const FORM_BOX_MIN_PX = 640;

export default function Contact({ darkMode, setDarkMode }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation("contact");
  const { t: tSeo } = useTranslation("seo");
  const gf = useMemo(() => getGoogleFormContactConfig(), []);
  const getRecaptchaToken = useRecaptchaToken();
  const [snack, setSnack] = useState({ open: false, isError: false, msg: "" });

  const validationSchema = useMemo(
    () =>
      Yup.object({
        name: Yup.string()
          .trim()
          .required(t("form.errors.required")),
        email: Yup.string()
          .trim()
          .email(t("form.errors.email"))
          .required(t("form.errors.required")),
        subject: Yup.string()
          .trim()
          .required(t("form.errors.required")),
        message: Yup.string()
          .trim()
          .min(10, t("form.errors.messageMin"))
          .required(t("form.errors.required")),
      }),
    [t]
  );

  const formik = useFormik({
    initialValues: { name: "", email: "", subject: "", message: "" },
    validationSchema,
    onSubmit: async (values, { resetForm, setSubmitting }) => {
      if (!gf.isReady) {
        setSubmitting(false);
        return;
      }
      try {
        const recaptchaToken = await getRecaptchaToken(RECAPTCHA_ACTIONS.CONTACT);
        if (process.env.REACT_APP_RECAPTCHA_SITE_KEY && !recaptchaToken) {
          setSnack({ open: true, isError: true, msg: t("form.errorSend") });
          return;
        }
        postGoogleFormInHiddenIframe(
          gf.actionUrl,
          gf.entries,
          values
        );
        setSnack({ open: true, isError: false, msg: t("form.success") });
        resetForm();
      } catch {
        setSnack({ open: true, isError: true, msg: t("form.errorSend") });
      } finally {
        setSubmitting(false);
      }
    },
  });

  const fieldProps = (field) => ({
    fullWidth: true,
    id: field,
    name: field,
    value: formik.values[field],
    onChange: formik.handleChange,
    onBlur: formik.handleBlur,
    error: formik.touched[field] && Boolean(formik.errors[field]),
    helperText: formik.touched[field] && formik.errors[field],
    disabled: !gf.isReady,
    variant: "outlined",
    sx: {
      mb: 2,
      "& .MuiOutlinedInput-root": {
        color: "white",
        "& fieldset": { borderColor: "rgba(255, 255, 255, 0.3)" },
        "&:hover fieldset": { borderColor: "rgba(255, 255, 255, 0.5)" },
      },
      "& .MuiInputLabel-root": { color: "rgba(255, 255, 255, 0.7)" },
    },
  });

  const contactInfo = useMemo(
    () => [
      {
        type: "email",
        icon: EmailIcon,
        title: t("email.title"),
        content: t("email.content"),
      },
      {
        type: "phone",
        icon: PhoneIcon,
        title: t("phone.title"),
        content: t("phone.content"),
      },
      {
        type: "address",
        icon: LocationOnIcon,
        title: t("address.title"),
        content: t("address.content"),
      },
    ],
    [t]
  );

  const ContactCard = ({ type, icon: Icon, title, content }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      viewport={{ once: true }}
      style={{ height: "100%" }}
    >
      <Box
        sx={{
          p: 4,
          background: "rgba(255, 255, 255, 0.05)",
          borderRadius: "20px",
          backdropFilter: "blur(10px)",
          border: "1px solid rgba(255, 255, 255, 0.1)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
          height: "100%",
          position: "relative",
          overflow: "hidden",
          transition: "all 0.3s ease",
          "&:hover": {
            transform: "translateY(-10px)",
            "& .icon-container": {
              transform: "scale(1.1) rotate(5deg)",
              background: `linear-gradient(135deg, ${colors.greenAccent[500]}, ${colors.blueAccent[400]})`,
            },
          },
        }}
      >
        <Box
          className="icon-container"
          sx={{
            width: 80,
            height: 80,
            borderRadius: "20px",
            background: "rgba(255, 255, 255, 0.1)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 2,
            transition: "all 0.3s ease",
          }}
        >
          <Icon
            sx={{ fontSize: 48, color: colors.greenAccent[400] }}
          />
        </Box>
        <Typography
          variant="h5"
          sx={{
            color: "white",
            fontWeight: 600,
            textAlign: "center",
            position: "relative",
            mb: 2,
            "&::after": {
              content: '""',
              position: "absolute",
              bottom: -8,
              left: "50%",
              transform: "translateX(-50%)",
              width: 40,
              height: 3,
              background: `linear-gradient(135deg, ${colors.greenAccent[500]}, ${colors.blueAccent[400]})`,
              borderRadius: "2px",
            },
          }}
        >
          {title}
        </Typography>
        {type === "phone" && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {content
              .split("\n")
              .map((phone) => phone.trim())
              .filter(Boolean)
              .map((phone) => (
                <Box
                  key={phone}
                  component="a"
                  href={`tel:${phone.replace(/[^\d+]/g, "")}`}
                  sx={{
                    color: "rgba(255, 255, 255, 0.8)",
                    textAlign: "center",
                    lineHeight: 1.6,
                    textDecoration: "none",
                    "&:hover": {
                      color: "white",
                      textDecoration: "underline",
                    },
                  }}
                >
                  {phone}
                </Box>
              ))}
          </Box>
        )}

        {type === "email" && (
          <Box
            component="a"
            href={`mailto:${content.trim()}`}
            sx={{
              color: "rgba(255, 255, 255, 0.8)",
              textAlign: "center",
              lineHeight: 1.6,
              textDecoration: "none",
              "&:hover": {
                color: "white",
                textDecoration: "underline",
              },
            }}
          >
            {content}
          </Box>
        )}

        {type === "address" && (
          <Typography
            sx={{
              color: "rgba(255, 255, 255, 0.8)",
              textAlign: "center",
              lineHeight: 1.6,
              whiteSpace: "pre-line",
            }}
          >
            {content}
          </Typography>
        )}
      </Box>
    </motion.div>
  );

  const formShellStyles = {
    p: 3,
    background: "rgba(255, 255, 255, 0.05)",
    borderRadius: "20px",
    backdropFilter: "blur(10px)",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    position: "relative",
    overflow: "visible",
    minHeight: FORM_BOX_MIN_PX,
  };

  return (
    <Box
      sx={{
        width: "100%",
        background: darkMode ? colors.primary[500] : "white",
        color: darkMode ? "white" : colors.grey[100],
      }}
    >
      <Seo
        path="/contact"
        title={tSeo("contact.title")}
        description={tSeo("contact.description")}
        keywords={tSeo("contact.keywords")}
        jsonLd={[ORGANIZATION_JSONLD, {
          "@context": "https://schema.org",
          "@type": "ContactPage",
          name: tSeo("contact.title"),
          description: tSeo("contact.description"),
          url: "https://patientmesh.com/contact",
        }]}
      />
      <iframe
        title="Google form gönderimi (gizli)"
        name={GOOGLE_FORM_HIDDEN_IFRAME_NAME}
        style={{
          position: "fixed",
          right: 0,
          bottom: 0,
          width: 0,
          height: 0,
          border: 0,
          opacity: 0,
          pointerEvents: "none",
          zIndex: -1,
        }}
        tabIndex={-1}
        aria-hidden="true"
      />

      <TopBarOfMarketing darkMode={darkMode} setDarkMode={setDarkMode} />

      <Box
        sx={{
          minHeight: "100vh",
          background: darkMode
            ? `linear-gradient(135deg, ${colors.primary[600]}, ${colors.greenAccent[700]})`
            : `linear-gradient(135deg, ${colors.primary[400]}, ${colors.greenAccent[500]})`,
          pt: "64px",
        }}
      >
        <Container maxWidth="lg" sx={{ py: 12 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <Typography
              variant="h1"
              sx={{
                color: "white",
                fontWeight: 700,
                textAlign: "center",
                mb: 3,
                fontFamily: "'Poppins', sans-serif",
              }}
            >
              {t("title")}
            </Typography>
            <Typography
              variant="h4"
              sx={{
                color: "rgba(255, 255, 255, 0.9)",
                textAlign: "center",
                mb: 8,
                maxWidth: "800px",
                mx: "auto",
                fontWeight: 300,
                lineHeight: 1.6,
              }}
            >
              {t("subtitle")}
            </Typography>
          </motion.div>

          <Grid container spacing={4} sx={{ mb: 8 }}>
            {contactInfo.map((info, index) => (
              <Grid item xs={12} md={4} key={index}>
                <ContactCard {...info} />
              </Grid>
            ))}
          </Grid>

          <Grid container spacing={4} alignItems="stretch">
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                style={{ height: "100%" }}
              >
                <Box
                  component="form"
                  onSubmit={formik.handleSubmit}
                  noValidate
                  sx={formShellStyles}
                >
                  {!gf.isReady && (
                    <Alert
                      severity="warning"
                      sx={{ mb: 2, textAlign: "left", "& .MuiAlert-message": { width: "100%" } }}
                    >
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ whiteSpace: "pre-line" }}
                      >
                        {t("form.configRequired")}
                      </Typography>
                    </Alert>
                  )}

                  <TextField
                    label={t("form.yourName")}
                    {...fieldProps("name")}
                    autoComplete="name"
                  />
                  <TextField
                    label={t("form.email")}
                    type="email"
                    {...fieldProps("email")}
                    autoComplete="email"
                  />
                  <TextField
                    label={t("form.subject")}
                    {...fieldProps("subject")}
                    autoComplete="off"
                  />
                  <TextField
                    label={t("form.yourMessage")}
                    {...fieldProps("message")}
                    multiline
                    minRows={4}
                  />

                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={!gf.isReady || formik.isSubmitting}
                    sx={{
                      mt: 1,
                      backgroundColor: "white",
                      color: colors.greenAccent[500],
                      padding: "12px 32px",
                      borderRadius: "30px",
                      fontSize: "1.1rem",
                      fontWeight: 600,
                      textTransform: "none",
                      "&:hover": {
                        backgroundColor: "rgba(255,255,255,0.9)",
                        transform: "translateY(-2px)",
                        boxShadow: "0 6px 20px rgba(0,0,0,0.15)",
                      },
                    }}
                  >
                    {t("form.send")}
                  </Button>
                </Box>
              </motion.div>
            </Grid>

            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                style={{ height: "100%" }}
              >
                <Box
                  sx={{
                    ...formShellStyles,
                    p: 2,
                    minHeight: FORM_BOX_MIN_PX,
                    display: "flex",
                    alignItems: "stretch",
                    "& iframe": {
                      flexGrow: 1,
                      minHeight: FORM_BOX_MIN_PX - 16,
                      width: "100%",
                      border: 0,
                      borderRadius: "16px",
                    },
                  }}
                >
                  <iframe
                    title={t("map.iframeTitle")}
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3011.0222604863247!2d28.88833037675798!3d41.00467897134731!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14caba23928b3173%3A0xba3c7e11d1c8c227!2sBiruni%20%C3%9Cniversitesi!5e0!3m2!1str!2str!4v1709853063011!5m2!1str!2str"
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </Box>
              </motion.div>
            </Grid>
          </Grid>
        </Container>

        <Snackbar
          open={snack.open}
          autoHideDuration={6000}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={() => setSnack((s) => ({ ...s, open: false }))}
            severity={snack.isError ? "error" : "success"}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {snack.msg}
          </Alert>
        </Snackbar>

        <Footer />
      </Box>
    </Box>
  );
}
